// for video-player

import { tryFunc, mkChangeChecker, throttle, debounce, once } from "./utils";
import { isPC } from "../../service/utils";

const wx: undefined | {ready: (fn: () => any) => void} = (window as any).wx

export type PlayerStatus = 'ready' | 'playing' | 'pausing' | 'loading' 

interface Handlers {
    status: ((status: PlayerStatus) => any)[]
    ready: (() => any)[]
    playing: (() => any)[]
    pausing: (() => any)[]
    loading: (() => any)[]

    ended: (() => any)[]
    timeUpdate: ((currentTime: number, duration: number) => any)[]
}

export class WeikeVideoPlyer {
    private readonly video: HTMLVideoElement = document.createElement('video')
    private status: PlayerStatus = 'pausing'
    private seekedTime: number = 0
    private readonly handlers: Handlers = {
        status: [], ready: [], playing: [], pausing: [], loading: [], ended: [], timeUpdate: []
    }
    private currentTime: number = 0
    private duration: number = 0

    private intervals: { epoll: number, play: number, seek: number } = { epoll: 0, play: 0, seek: 0 }

    // public static readonly _emptyAudio: string = 'data:video/mpeg;base64,//FsgAPf/N4CAExhdmM1Ny4zOC4xMDAAQiAIwRg4//FsgAG//CEQBGCMHA=='
    private initReady: boolean = false
    private afterInitQueue: (() => any)[] = []


    constructor(autoInit: boolean = true, videoElement: HTMLVideoElement) {
        if (autoInit) {
            this.init()
        }
        this.video = videoElement
        this.afterInit(() => {
            const checkTimeChange = mkChangeChecker(0)
            const checkStatusChange = mkChangeChecker()
            const checkTimeUpdateChange = mkChangeChecker()

            const emitTime = throttle((t: number, d: number) => {
                const key = `${t},${d}`
                if (checkTimeUpdateChange(key)) {
                    this.currentTime = t
                    this.duration = d
                    this.emit('timeUpdate', [t, d])
                }
            }, 300)

            const checkEmitStatusChange = mkChangeChecker()
            const emitStatus = debounce(() => {
                if (checkEmitStatusChange(this.status)) {
                    this.emit('status', [this.status])
                    this.emit(this.status, [])
                }
            }, 50)

            this.setInterval('epoll', () => {
                const curr = tryFunc(() => this.video.currentTime) || 0
                const dur = tryFunc(() => this.video.duration) || 0

                emitTime(this.intervals.seek > 0 ? this.seekedTime : Math.floor(curr), Math.floor(dur))
                if (checkTimeChange(curr)) {
                    this.status = "playing"
                } else {
                    if (this.video.paused) {
                        this.status = 'pausing'
                    } else {
                        this.status = 'loading'
                    }
                }

                if (checkStatusChange(this.status)) {
                    emitStatus()
                }
            }, 30)

            this.video.addEventListener('ended', () => {
                this.emit('timeUpdate', [this.currentTime, this.duration])
                this.emit('ended', [])
            })
        })
    }


    public init = once(() => {
        const initVideo = () => {
            this.video.muted = true
            if (this.video.currentTime > 0) {
                this.pause()
                this.video.muted = false
                this.video.removeEventListener('timeupdate', initVideo)
                this.emit('ready', [])
            }
        }

        const initReady = () => {
            if (!this.initReady) {
                this.video.pause()
                this.initReady = true
                this.afterInitQueue.forEach(fn => tryFunc(fn))

                // 初始化视频
                this.video.play()
                this.video.addEventListener('timeupdate', initVideo)

                document.removeEventListener('touchstart', initReady)
                document.removeEventListener('mousedown', initReady)
            }
        }

        document.addEventListener('touchstart', initReady)
        document.addEventListener('mousedown', initReady)
        
        if (isPC()) {
            document.dispatchEvent(new Event('mousedown'))
            document.dispatchEvent(new Event('touchstart'))
        }

        if (wx) {
            wx.ready(initReady)
        }

        try {
            this.video.play()
                .then(initReady)
                .catch(() => void (0))
        } catch (err) {
            if (!this.video.paused) {
                initReady()
            }
        }

    })

    private afterInit(fn: () => any) {
        if (this.initReady) {
            fn()
        } else {
            this.afterInitQueue.push(fn)
        }
    }
    
    private setInterval<K extends keyof WeikeVideoPlyer['intervals']>(key: K, fn: () => any, timeout: number) {
        fn()
        this.intervals[key] = window.setInterval(fn, timeout)
    }

    private clearInterval<K extends keyof WeikeVideoPlyer['intervals']>(key: K) {
        window.clearInterval(this.intervals[key])
        this.intervals[key] = 0
    }

    private emit<Event extends keyof Handlers>(event: Event, args: Parameters<Handlers[Event][0]>) {
        this.handlers[event].forEach((handler: Handlers[Event][0]) => (handler as any)(...args))
    }

    public load(src: string) {
        this.afterInit(() => {
            this.clearInterval('play')
            this.clearInterval('seek')
            this.video.src = src
            tryFunc(() => this.video.load())
        })
    }

    
    public play() {
        this.afterInit(() => {
            this.clearInterval('play')
            this.setInterval('play', () => {
                this.video.paused
                    ? tryFunc(() => this.video.play())
                    : this.clearInterval('play')
            }, 50)
        })
    }

    
    public setPlayRate(rate: number) {
        this.video.playbackRate = rate
    }

    public toggleFullscreen() {
        if (!document.fullscreenElement) {
            requestFullscreen(this.video)
          } else {
            exitFullscreen(document)
          }
    }

    public seek(seekedTime: number) {
        this.afterInit(() => {
            this.clearInterval('seek')
            this.seekedTime = seekedTime

            this.setInterval('seek', () => { 
                const currentTime = tryFunc(() => this.video.currentTime) || 0
                const duration = tryFunc(() => this.video.duration) || 0

                if ((Math.abs(currentTime - seekedTime) < 0.3)
                    || (duration > 0 && seekedTime >= duration)) {
                    this.clearInterval('seek')
                } else {
                    tryFunc(() => this.video.currentTime = seekedTime)
                }
            }, 50)

            setTimeout(() => this.emit('timeUpdate', [
                Math.floor(tryFunc(() => this.video.currentTime) || 0),
                Math.floor(tryFunc(() => this.video.duration) || 0),
            ]), 60)
        })
    }

    public playbackRate(rate?: number) {
        if (rate) {
            this.video.playbackRate = rate
            this.video.defaultPlaybackRate = rate
        }
        return this.video.playbackRate
    }

    public pause() {
        this.afterInit(() => {
            this.clearInterval('play')
            tryFunc(() => this.video.pause())
        })
    }

    public on<Event extends keyof Handlers>(event: Event, handler: Handlers[Event][0]) {
        this.handlers[event].push(handler as any)
    }

    public off<Event extends keyof Handlers>(event: Event) {
        this.handlers[event] = []
    }

    public getStatus() {
        return {
            status: this.status,
            currentTime: tryFunc(() => this.video.currentTime) || 0,
            duration: tryFunc(() => this.video.duration) || 0,
        }
    }
}


//进入全屏
function requestFullscreen(element: any) {
    //此方法不可以在異步任務中執行，否則火狐無法全屏
    if (element.webkitEnterFullScreen) {
        element.webkitEnterFullScreen()
    } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen()
    } else if (element.requestFullscreen) {
        element.requestFullscreen()
    } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen()
    } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen()
    } else if (element.oRequestFullscreen) {
        element.oRequestFullscreen()
    }
    else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullScreen()
    }
}

//退出全屏
function exitFullscreen(element: any) {
    if (element.exitFullscreen) {
        element.exitFullscreen()
    } else if (element.msExitFullscreen) {
        element.msExitFullscreen()
    } else if (element.mozCancelFullScreen) {
        element.mozCancelFullScreen()
    } else if (element.oRequestFullscreen) {
        element.oCancelFullScreen()
    } else if (element.webkitExitFullscreen) {
        element.webkitExitFullscreen()
    }
}


// WEBPACK FOOTER //
// ../src/media-player/video-player/index.ts